import { FormLabel, Grid, Stack, Typography } from '@mui/material';
import CustomDateTimeRangePicker from 'common/components/CustomDateTimeRangePicker';
import CalendarType from 'common/enums/calendarTypes';
import useFlexBoxStyles from 'common/flexBox';
import { useIncludeDateValidation } from 'common/hooks/useIncludeDateValidation';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const transformDates = (dates) => {
    return dates.map((dateRange) => ({
        startDate: dateRange.startAt,
        endDate: dateRange.endAt,
    }));
};

const OutOfTheOffice = ({ fields, handleFieldChange, timeZone, type }) => {
    const { t } = useTranslation();
    const { classes: flexBox } = useFlexBoxStyles();
    const { isValidDateRange, error, validationType, setError } = useIncludeDateValidation();
    const dates = fields?.unavailableDates || [];

    const handleEditDate = (newDate, idx) => {
        const newRange = [
            ...dates.slice(0, idx),
            { startAt: moment.tz(newDate.start, timeZone), endAt: moment.tz(newDate.end, timeZone) },
            ...dates.slice(idx + 1),
        ];
        saveRange(newRange);
    };

    const handleAddDate = (newDate) => {
        const newRange = [...dates, { startAt: moment.tz(newDate.start, timeZone), endAt: moment.tz(newDate.end, timeZone) }];
        saveRange(newRange);
    };

    const handleDeleteDate = (idx) => {
        if (error?.idx === idx) {
            setError(null);
        }
        const newRange = [...dates.slice(0, idx), ...dates.slice(idx + 1)];
        saveRange(newRange);
    };

    const saveRange = (data) => {
        handleFieldChange({
            target: {
                name: 'unavailableDates',
                value: data,
            },
        });
    };

    const handleChange = (date, idx, callback) => {
        const ranges = transformDates(dates);
        const isValid = isValidDateRange({ ranges, date, idx, type: validationType.UNSELECTED });
        if (isValid) {
            if (idx === -1) {
                handleAddDate(date);
                callback?.();
            } else {
                handleEditDate(date, idx);
            }
        }
    };

    return (
        <Stack direction="column" spacing={1} className={flexBox.paddingTop2}>
            <FormLabel>{t('services.form.card.excludeRangeDate')}</FormLabel>
            <Grid container>
                <Grid item sm={1.5}>
                    <Typography variant="subtitle2" fontWeight={400} className={flexBox.paddingTop1}>
                        {t('services.form.dateFrom').toUpperCase()}
                    </Typography>
                </Grid>

                <Grid item sm={10.5}>
                    <Stack direction="column" spacing={1}>
                        {dates?.map((date, idx) => {
                            return (
                                <CustomDateTimeRangePicker
                                    key={idx}
                                    date={{
                                        start: moment.tz(date.startAt, timeZone).format('YYYY-MM-DDTHH:mm'),
                                        end: moment.tz(date.endAt, timeZone).format('YYYY-MM-DDTHH:mm'),
                                    }}
                                    dates={[date.startAt, date.endAt]}
                                    onApply={(date) => handleChange(date, idx)}
                                    onDelete={() => handleDeleteDate(idx)}
                                    error={error?.idx === idx ? error?.msg : null}
                                    disabled={error ? error?.idx !== idx : false}
                                ></CustomDateTimeRangePicker>
                            );
                        })}

                        <Stack direction="column" style={dates.length === 0 ? {} : { paddingRight: '38px' }}>
                            <CustomDateTimeRangePicker
                                onApply={(date) => handleChange(date, -1)}
                                error={error?.idx === -1 ? error?.msg : null}
                                disabled={error ? error?.idx !== -1 : false}
                                dates={dates}
                            ></CustomDateTimeRangePicker>
                        </Stack>

                        <Typography display="block" gutterBottom component="div">
                            {type === CalendarType.AGENT
                                ? t('calendar.agent.rangeNote')
                                : t('calendar.asset.rangeNote')}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>
        </Stack>
    );
};

export default OutOfTheOffice;
